/**
 * SearchableDropdown Component
 * A custom searchable dropdown that looks like Fluent UI Dropdown
 * but includes search/filter functionality inside the popup
 * Styled to match Microsoft 365 admin console appearance
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  makeStyles,
  tokens,
  Popover,
  PopoverTrigger,
  PopoverSurface,
  Input,
  mergeClasses,
} from '@fluentui/react-components';
import { ChevronDownRegular, SearchRegular, CheckmarkRegular } from '@fluentui/react-icons';

const useStyles = makeStyles({
  trigger: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    minHeight: '32px',
    paddingLeft: '12px',
    paddingRight: '12px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderTopWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderRightWidth: '1px',
    borderTopStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderRightStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke1,
    borderBottomColor: tokens.colorNeutralStroke1,
    borderLeftColor: tokens.colorNeutralStroke1,
    borderRightColor: tokens.colorNeutralStroke1,
    borderTopLeftRadius: tokens.borderRadiusMedium,
    borderTopRightRadius: tokens.borderRadiusMedium,
    borderBottomLeftRadius: tokens.borderRadiusMedium,
    borderBottomRightRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    fontSize: tokens.fontSizeBase300,
    fontFamily: tokens.fontFamilyBase,
    color: tokens.colorNeutralForeground1,
    transitionProperty: 'border-color',
    transitionDuration: '0.1s',
    boxSizing: 'border-box',
  },
  triggerHover: {
    borderTopColor: tokens.colorNeutralStroke1Hover,
    borderBottomColor: tokens.colorNeutralStroke1Hover,
    borderLeftColor: tokens.colorNeutralStroke1Hover,
    borderRightColor: tokens.colorNeutralStroke1Hover,
  },
  triggerDisabled: {
    backgroundColor: tokens.colorNeutralBackgroundDisabled,
    borderTopColor: tokens.colorNeutralStrokeDisabled,
    borderBottomColor: tokens.colorNeutralStrokeDisabled,
    borderLeftColor: tokens.colorNeutralStrokeDisabled,
    borderRightColor: tokens.colorNeutralStrokeDisabled,
    color: tokens.colorNeutralForegroundDisabled,
    cursor: 'not-allowed',
  },
  triggerOpen: {
    borderTopColor: tokens.colorBrandStroke1,
    borderBottomColor: tokens.colorBrandStroke1,
    borderLeftColor: tokens.colorBrandStroke1,
    borderRightColor: tokens.colorBrandStroke1,
  },
  triggerText: {
    flex: 1,
    textAlign: 'left',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  placeholder: {
    color: tokens.colorNeutralForeground4,
  },
  chevron: {
    marginLeft: '8px',
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    transitionProperty: 'transform',
    transitionDuration: '0.1s',
  },
  chevronOpen: {
    transform: 'rotate(180deg)',
  },
  popoverSurface: {
    paddingTop: '0',
    paddingBottom: '0',
    paddingLeft: '0',
    paddingRight: '0',
    minWidth: '200px',
    maxHeight: '300px',
    overflow: 'hidden',
    boxShadow: tokens.shadow16,
  },
  searchContainer: {
    paddingTop: '8px',
    paddingBottom: '8px',
    paddingLeft: '8px',
    paddingRight: '8px',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke2,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  searchInput: {
    width: '100%',
  },
  optionsList: {
    maxHeight: '220px',
    overflowY: 'auto',
    paddingTop: '4px',
    paddingBottom: '4px',
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: '8px',
    paddingBottom: '8px',
    paddingLeft: '12px',
    paddingRight: '12px',
    cursor: 'pointer',
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    backgroundColor: 'transparent',
    borderTopStyle: 'none',
    borderBottomStyle: 'none',
    borderLeftStyle: 'none',
    borderRightStyle: 'none',
    width: '100%',
    textAlign: 'left',
    fontFamily: tokens.fontFamilyBase,
  },
  optionHover: {
    backgroundColor: tokens.colorNeutralBackground1Hover,
  },
  optionSelected: {
    backgroundColor: tokens.colorNeutralBackground1Selected,
    fontWeight: tokens.fontWeightSemibold,
  },
  optionDisabled: {
    color: tokens.colorNeutralForegroundDisabled,
    cursor: 'not-allowed',
  },
  optionText: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  checkmark: {
    marginRight: '8px',
    fontSize: '12px',
    color: tokens.colorBrandForeground1,
    visibility: 'hidden',
  },
  checkmarkVisible: {
    visibility: 'visible',
  },
  noResults: {
    paddingTop: '12px',
    paddingBottom: '12px',
    paddingLeft: '12px',
    paddingRight: '12px',
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
});

export interface DropdownOption {
  id: string | number;
  label: string;
  disabled?: boolean;
}

export interface SearchableDropdownProps {
  options: DropdownOption[];
  selectedValue: string;
  onSelect: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  style?: React.CSSProperties;
  clearable?: boolean;
}

export const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  options,
  selectedValue,
  onSelect,
  placeholder = 'Select an option',
  disabled = false,
  style,
  clearable = false,
}) => {
  const styles = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const selectedOption = options.find(opt => String(opt.id) === selectedValue);

  const filteredOptions = options.filter(opt => {
    const label = opt.label ?? '';
    return label.toLowerCase().includes(searchQuery.toLowerCase());
  });

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
    }
  }, [isOpen]);

  const handleOptionClick = (option: DropdownOption) => {
    if (option.disabled) {
      return;
    }
    onSelect(String(option.id));
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      triggerRef.current?.focus();
    }
  };

  return (
    <Popover
      open={isOpen}
      onOpenChange={(_, data) => !disabled && setIsOpen(data.open)}
      positioning={{
        position: 'below',
        align: 'start',
        autoSize: 'width',
        overflowBoundaryPadding: 16,
      }}
      trapFocus
    >
      <PopoverTrigger disableButtonEnhancement>
        <button
          ref={triggerRef}
          type="button"
          className={mergeClasses(
            styles.trigger,
            disabled && styles.triggerDisabled,
            isOpen && styles.triggerOpen
          )}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          style={style}
        >
          <span className={mergeClasses(styles.triggerText, !selectedOption && styles.placeholder)}>
            {selectedOption?.label || placeholder}
          </span>
          <ChevronDownRegular
            className={mergeClasses(styles.chevron, isOpen && styles.chevronOpen)}
          />
        </button>
      </PopoverTrigger>

      <PopoverSurface className={styles.popoverSurface} onKeyDown={handleKeyDown}>
        <div className={styles.searchContainer}>
          <Input
            ref={searchInputRef}
            className={styles.searchInput}
            placeholder="Search..."
            value={searchQuery}
            onChange={(_, data) => setSearchQuery(data.value)}
            contentBefore={<SearchRegular />}
            size="small"
          />
        </div>

        <div className={styles.optionsList} role="listbox">
          {clearable && selectedValue && (
            <button
              type="button"
              role="option"
              className={styles.option}
              onClick={() => {
                onSelect('');
                setIsOpen(false);
                setSearchQuery('');
              }}
            >
              <span className={styles.optionText} style={{ color: '#605e5c', fontStyle: 'italic' }}>
                Clear selection
              </span>
            </button>
          )}
          {filteredOptions.length === 0 ? (
            <div className={styles.noResults}>No results found</div>
          ) : (
            filteredOptions.map(option => (
              <button
                key={option.id}
                type="button"
                role="option"
                aria-selected={String(option.id) === selectedValue}
                className={mergeClasses(
                  styles.option,
                  String(option.id) === selectedValue && styles.optionSelected,
                  option.disabled && styles.optionDisabled
                )}
                onClick={() => handleOptionClick(option)}
                disabled={option.disabled}
              >
                <CheckmarkRegular
                  className={mergeClasses(
                    styles.checkmark,
                    String(option.id) === selectedValue && styles.checkmarkVisible
                  )}
                />
                <span className={styles.optionText}>{option.label}</span>
              </button>
            ))
          )}
        </div>
      </PopoverSurface>
    </Popover>
  );
};

export default SearchableDropdown;
